import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getJourneyTimeString } from "../../../functions/stringFunctions";
import { CHOICE_CONDITION_TYPE, JOURNEY_REWARD_TYPE, LOCALES } from "../../../constants";
import { loadData } from "../../../functions/dataLoader";
import FILTERS from "../../../config/filter_config.json";

const getMonthPriority = (monthStr) => {
    const month = parseInt(monthStr.replace("월", ""));
    return month >= 3 ? month - 3 : month + 9;
};

const getPeriodPriority = (periodStr) => {
    if (periodStr === "초순") return 0;
    if (periodStr === "중순") return 1;
    if (periodStr === "하순") return 2;
    return 3;
};

const sortJourneys = (originData) => (a, b) => {
    const aObj = originData[a][0];
    const bObj = originData[b][0];
    const aTime = aObj.times?.length > 0 ? aObj.times[0] : null;
    const bTime = bObj.times?.length > 0 ? bObj.times[0] : null;
    if (!aTime && !bTime) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    const [aMonth, aPeriod] = aTime.split(" ");
    const [bMonth, bPeriod] = bTime.split(" ");
    const aMonthP = getMonthPriority(aMonth);
    const bMonthP = getMonthPriority(bMonth);
    if (aMonthP !== bMonthP) return aMonthP - bMonthP;
    return getPeriodPriority(aPeriod) - getPeriodPriority(bPeriod);
};

const normalizeJourneys = (originData, journeyItems, potentials, statPotentials, buffs) => {
    const entities = {};
    const ids = [];

    const itemMap = new Map(journeyItems.map((item) => [item.id, item]));
    const potentialMap = new Map(potentials.map((p) => [p.id, p]));
    const statPotentialMap = new Map(statPotentials.map((sp) => [sp.id, sp]));
    const buffMap = new Map(buffs.map((b) => [b.id, b]));

    Object.entries(originData).forEach(([journeyName, journeys]) => {
        const base = journeys[0];
        const parts = [];
        const parts_ENG = [];

        parts.push(base.name[LOCALES.KO]);
        parts_ENG.push(base.name[LOCALES.EN]);
        if (base.times) {
            base.times.forEach((time) => {
                parts.push(time);
                parts_ENG.push(getJourneyTimeString(LOCALES.EN, time));
            });
        }

        const variants = journeys.map((journey) => {
            journey.choices.forEach((choice) => {
                if (choice.name !== "") {
                    parts.push(choice.name[LOCALES.KO]);
                    parts_ENG.push(choice.name[LOCALES.EN]);
                }

                if (choice.condition) {
                    if (choice.condition.type.includes("ITEM")) {
                        const item = itemMap.get(Number(choice.condition.target));
                        if (item) {
                            parts.push(item.name[LOCALES.KO]);
                            parts_ENG.push(item.name[LOCALES.EN]);
                            choice.condition.item = item;
                        }
                    } else {
                        const value = choice.condition.value > 0 ? `+${choice.condition.value}` : choice.condition.value;
                        parts.push(CHOICE_CONDITION_TYPE[choice.condition.type][LOCALES.KO], value);
                        parts_ENG.push(CHOICE_CONDITION_TYPE[choice.condition.type][LOCALES.EN], value);
                    }
                }

                const failes = choice.failure_rewards ?? [];
                [...choice.success_rewards, ...failes].forEach((rewards) => {
                    rewards.forEach((reward) => {
                        let obj;
                        if (reward.type === "RT_JOURNEY_ITEM") obj = itemMap.get(reward.reward_id);
                        else if (reward.type === "RT_SE_POTEN") obj = potentialMap.get(reward.reward_id);
                        else if (reward.type === "RT_STAT_POTEN") obj = statPotentialMap.get(reward.reward_id);
                        else if (reward.type === "RT_JOURNEY_BUFF") obj = buffMap.get(reward.reward_id);

                        if (obj) {
                            parts.push(obj.name[LOCALES.KO]);
                            parts_ENG.push(obj.name[LOCALES.EN]);
                            reward.targetObj = obj;
                        } else {
                            parts.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.KO]);
                            parts_ENG.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.EN]);
                        }
                    });
                });
            });

            let battleNames = journey.battle_names ?? null;
            if (battleNames && battleNames.length > 1) {
                const common = battleNames[0][LOCALES.KO].replace(" Ⅰ", "");
                const common_ENG = battleNames[0][LOCALES.EN].replace(" Ⅰ", "");
                battleNames = battleNames.map((name, idx) =>
                    idx > 0 ? { "ko-KR": name[LOCALES.KO].replace(common, ""), "en-US": name[LOCALES.EN].replace(common_ENG, "") } : name,
                );
            }

            return {
                difficulties: journey.difficulties ?? null,
                battleNames,
                choices: journey.choices,
            };
        });

        entities[journeyName] = {
            id: base.id,
            name: base.name,
            type: base.type,
            times: base.times ?? [],
            variants,
            searchText: parts.join(" "),
            searchText_ENG: parts_ENG.join(" "),
        };

        ids.push(journeyName);
    });

    ids.sort(sortJourneys(originData));

    return { entities, ids };
};

export const initJourneys = createAsyncThunk("journeys/init", async () => {
    const [originData, journeyItems, potentials, statPotentials, buffs] = await Promise.all([
        loadData("journeys.json"),
        loadData("journey_items.json"),
        loadData("potentials.json"),
        loadData("stat_potentials.json"),
        loadData("journey_buffs.json"),
    ]);
    const normalized = normalizeJourneys(originData, journeyItems, potentials, statPotentials, buffs);
    return normalized;
});

const journeySlice = createSlice({
    name: "journeys",
    initialState: {
        entities: {},
        ids: [],
        filter: {
            tab: FILTERS.JOURNEY_GROUP[0],
            difficulty: FILTERS.JOURNEY_DIFFICULTY[0],
        },
        status: "idle",
    },
    reducers: {
        changeTab: (state, action) => {
            state.filter.tab = action.payload;
        },
        changeDifficulty: (state, action) => {
            state.filter.difficulty = action.payload.obj;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initJourneys.fulfilled, (state, action) => {
            const normalized = action.payload;
            state.entities = normalized.entities;
            state.ids = normalized.ids;
            // state.filter = {
            //     tab: FILTERS.JOURNEY_GROUP[0],
            //     difficulty: FILTERS.JOURNEY_DIFFICULTY[0],
            // };
            state.status = "done";
        });
    },
});

export const { changeTab, changeDifficulty } = journeySlice.actions;
export default journeySlice.reducer;
